import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box } from '@mui/material';
import { useIsMobile } from '../theme';

// Strips the extension and tidies a picked file's name into a usable default —
// on a phone the file is typically "IMG_4821.HEIC", so making the author type a
// name from scratch before every upload is the difference between two taps and
// a chore.
const suggestFilename = (file) =>
    file
        ? file.name
              .replace(/\.[^.]+$/, '')
              .replace(/[_\s]+/g, '-')
              .toLowerCase()
        : '';

const ImageUploadDialog = ({ open, onClose, file, onSubmit }) => {
    const isMobile = useIsMobile();
    const [filename, setFilename] = useState('');
    const [alt, setAlt] = useState('');

    const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

    // Prefill from the picked file each time the dialog opens with a new one.
    useEffect(() => {
        if (file) setFilename(suggestFilename(file));
    }, [file]);

    // Clean up preview URL when the file changes or the dialog closes
    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!filename.trim() || !alt.trim()) return;
        onSubmit({ filename: filename.trim(), alt: alt.trim() });
        setFilename('');
        setAlt('');
    };

    const handleClose = () => {
        setFilename('');
        setAlt('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth fullScreen={isMobile}>
            <DialogTitle>Detalhes da imagem</DialogTitle>
            <DialogContent>
                <Box component='form' onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {previewUrl && (
                        <Box
                            component='img'
                            src={previewUrl}
                            alt='Pré-visualização'
                            sx={{ width: '100%', maxHeight: { xs: '35vh', md: 220 }, objectFit: 'contain', borderRadius: 1.5, bgcolor: '#f2f0eb' }}
                        />
                    )}
                    <TextField
                        autoFocus={!isMobile}
                        label='Nome do ficheiro'
                        fullWidth
                        value={filename}
                        onChange={(e) => setFilename(e.target.value)}
                        required
                        helperText='Sem extensão. É guardado como .webp.'
                    />
                    <TextField
                        label='Texto alternativo'
                        fullWidth
                        value={alt}
                        onChange={(e) => setAlt(e.target.value)}
                        required
                        multiline
                        rows={2}
                        helperText='Descreva a imagem — é o que leem os motores de busca e os leitores de ecrã.'
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
                <Button onClick={handleClose} color='inherit'>
                    Cancelar
                </Button>
                <Button onClick={handleSubmit} variant='contained' disabled={!filename.trim() || !alt.trim()}>
                    Carregar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImageUploadDialog;
